import { Button, Popconfirm, Switch, Table, message, Input, Tooltip, Dropdown } from "antd";
import React, { useEffect, useState } from "react";
import { DeleteOutlined, CopyOutlined, SyncOutlined, KeyOutlined } from "@ant-design/icons";
import { apiRequests } from "src/shared/api/api";
import { addRow } from "src/shared";
import CommerceMLModalCreate from "./CommerceMLModalCreate";
import CommerceMLModalUpdate from "./CommerceMLModalUpdate";
import dayjs from "dayjs";

// API может отдавать active или is_active, 0/1 или true/false — приводим к одному виду для таблицы и модалки
const normalizeBool = (v) => v === true || v === 1 || v === "true" || v === "1";
function normalizeConnection(c) {
  const active = normalizeBool(c?.active ?? c?.is_active);
  return {
    ...c,
    active,
    is_active: active, // на случай если бэкенд читает только is_active
    import_products: normalizeBool(c?.import_products),
    export_products: normalizeBool(c?.export_products),
    import_orders: normalizeBool(c?.import_orders),
    export_orders: normalizeBool(c?.export_orders),
  };
}

function CommerceML({ token }) {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);

  const getConnections = async () => {
    setLoading(true);
    try {
      const response = await apiRequests.commerceml.getConnections(token);
      const list = (response.data || []).map(normalizeConnection);
      setConnections(list);
    } catch (error) {
      message.error("Ошибка загрузки подключений");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlerSwitchChange = async (record, field, value) => {
    try {
      const boolValue = !!value;
      const payload = { [field]: boolValue };
      if (field === "active") payload.is_active = boolValue; // бэкенд может ожидать is_active
      await apiRequests.commerceml.updateConnection(record.id, payload, token);
      message.success("Настройки обновлены");
      getConnections();
    } catch (error) {
      message.error("Ошибка обновления настроек");
    }
  };

  // Режим обмена "1 из 4": 0 = всё выкл, 1..4 = кол-во включённых (приём товаров → выгрузка товаров → приём заказов → выгрузка заказов)
  const exchangeFlagsToLevel = (r) => {
    const flags = [!!r.import_products, !!r.export_products, !!r.import_orders, !!r.export_orders];
    return flags.filter(Boolean).length;
  };
  const exchangeLevelToFlags = (level) => ({
    import_products: level >= 1,
    export_products: level >= 2,
    import_orders: level >= 3,
    export_orders: level >= 4,
  });
  const handleExchangeLevelChange = async (record, delta) => {
    const current = exchangeFlagsToLevel(record);
    const next = Math.max(0, Math.min(4, current + delta));
    if (next === current) return;
    try {
      await apiRequests.commerceml.updateConnection(record.id, exchangeLevelToFlags(next), token);
      message.success("Настройки обновлены");
      getConnections();
    } catch (error) {
      message.error("Ошибка обновления настроек");
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiRequests.commerceml.deleteConnection(id, token);
      message.success("Подключение удалено");
      getConnections();
    } catch (error) {
      message.error("Ошибка удаления подключения");
    }
  };

  // Синхронизация таблицы с модалкой: что сохранили в модалке — сразу отображаем в таблице (используем функциональное обновление, чтобы не зависеть от замыкания)
  const handleConnectionUpdated = (updatedRecord) => {
    if (updatedRecord.id == null) return;
    setConnections((prev) => {
      const next = [...prev];
      const index = next.findIndex((c) => c.id === updatedRecord.id);
      if (index === -1) return prev;
      next[index] = { ...next[index], ...updatedRecord };
      return next;
    });
  };

  const handleConnectionCreated = (newRecord) => {
    addRow(connections, [newRecord], setConnections);
  };

  const [resettingPassword, setResettingPassword] = useState(null);
  const handleResetPassword = async (record) => {
    setResettingPassword(record.id);
    try {
      console.log("Сброс пароля для подключения:", record.id);
      const response = await apiRequests.commerceml.resetPassword(record.id, null, token);
      console.log("Ответ от сервера:", response);
      const { data } = response;
      const newPassword = data?.password ?? data?.new_password;
      if (newPassword) {
        message.success({
          content: (
            <span>
              Пароль сброшен. Новый пароль: <strong style={{ userSelect: "all" }}>{newPassword}</strong> — скопируйте (отображается 8 сек), он также в колонке «Пароль».
            </span>
          ),
          duration: 8,
        });
      } else {
        message.success({ content: "Пароль сброшен. Новый пароль в колонке «Пароль».", duration: 8 });
      }
      getConnections();
    } catch (error) {
      console.error("Ошибка сброса пароля:", error);
      console.error("Response:", error.response);
      let detail = error.response?.data?.detail ?? error.response?.data?.message ?? error.message;
      if (Array.isArray(detail)) detail = detail.map((d) => (d?.msg ?? d)).join("; ");
      if (detail && typeof detail === "object") detail = JSON.stringify(detail);
      const text = detail && String(detail).trim() 
        ? `Ошибка сброса пароля: ${String(detail)}` 
        : "Ошибка сброса пароля. Проверьте консоль браузера (F12) и убедитесь, что бэкенд реализует POST /commerceml/connections/:id/reset-password";
      message.error({
        content: text,
        duration: 10,
      });
    } finally {
      setResettingPassword(null);
    }
  };

  const [syncing, setSyncing] = useState(null); // { id, type } когда идёт синхронизация

  const handleSync = async (id, type) => {
    const typeNames = {
      catalog: "товаров (выгрузка)",
      sale: "заказов (выгрузка)",
      import_catalog: "товаров (загрузка)",
      import_sale: "заказов (загрузка)",
    };
    const typeName = typeNames[type] || type;
    const msgKey = `sync-${id}-${type}`;
    setSyncing({ id, type });
    try {
      message.loading({ content: `Синхронизация ${typeName}...`, key: msgKey });
      const response = await apiRequests.commerceml.syncConnection(id, type, token);
      const backendMessage = response?.data?.message;
      if (backendMessage) {
        message.success({
          content: backendMessage,
          key: msgKey,
          duration: 8,
        });
      } else {
        message.success({ content: `Синхронизация ${typeName} завершена`, key: msgKey, duration: 3 });
      }
      getConnections();
    } catch (error) {
      const detail = error.response?.data?.detail ?? error.response?.data?.message ?? error.message;
      const isBadRequest = error.response?.status === 400;
      const content = typeof detail === "string" ? detail : `Ошибка синхронизации: ${detail}`;
      if (isBadRequest) {
        message.warning({ content, key: msgKey, duration: 6 });
      } else {
        message.error({ content: content || "Ошибка синхронизации", key: msgKey, duration: 6 });
      }
    } finally {
      setSyncing(null);
    }
  };

  const onSyncMenuClick = ({ key }) => {
    const [type, idStr] = String(key).split(":");
    const id = parseInt(idStr, 10);
    if (!type || isNaN(id)) return;
    handleSync(id, type);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success("Скопировано в буфер обмена");
  };

  const getConnectionUrl = (connectionId) => {
    const baseUrl = process.env.REACT_APP_APP_URL || window.location.host;
    const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
    return `${protocol}://${baseUrl}/api/v1/commerceml/${connectionId}`;
  };

  const columns = [
    { title: "ID", dataIndex: "id", width: 80 },
    { 
      title: "Название", 
      dataIndex: "name",
      render: (text) => <strong>{text}</strong>
    },
    {
      title: "URL для подключения",
      dataIndex: "id",
      render: (id) => {
        const url = getConnectionUrl(id);
        return (
          <Tooltip title="Нажмите, чтобы скопировать">
            <Input
              value={url}
              readOnly
              addonAfter={
                <Button
                  type="text"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(url)}
                  size="small"
                />
              }
              style={{ cursor: "pointer" }}
              onClick={() => copyToClipboard(url)}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Логин",
      dataIndex: "username",
      render: (text) => (
        <Tooltip title="Нажмите, чтобы скопировать">
          <Input
            value={text}
            readOnly
            addonAfter={
              <Button
                type="text"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(text)}
                size="small"
              />
            }
            style={{ cursor: "pointer", width: 150 }}
            onClick={() => copyToClipboard(text)}
          />
        </Tooltip>
      ),
    },
    {
      title: "Пароль",
      dataIndex: "password",
      render: (text) => (
        <Tooltip title="Нажмите, чтобы скопировать">
          <Input.Password
            value={text}
            readOnly
            addonAfter={
              <Button
                type="text"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(text)}
                size="small"
              />
            }
            style={{ cursor: "pointer", width: 150 }}
            onClick={() => copyToClipboard(text)}
          />
        </Tooltip>
      ),
    },
    {
      title: "Активно",
      dataIndex: "active",
      width: 100,
      render: (_, record) => (
        <Switch
          checked={!!(record.active ?? record.is_active)}
          onChange={(checked) => handlerSwitchChange(record, "active", checked)}
        />
      ),
    },
    {
      title: "Режим обмена",
      key: "exchange_level",
      width: 140,
      render: (_, record) => {
        const level = exchangeFlagsToLevel(record);
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Button
              type="text"
              size="small"
              disabled={level <= 0}
              onClick={() => handleExchangeLevelChange(record, -1)}
              style={{ minWidth: 28, padding: "0 4px" }}
            >
              −
            </Button>
            <span style={{ minWidth: 56, textAlign: "center", fontWeight: 500 }} title="Включено режимов обмена: Принимать товары, Выгружать товары, Принимать заказы, Выгружать заказы">
              {level} из 4
            </span>
            <Button
              type="text"
              size="small"
              disabled={level >= 4}
              onClick={() => handleExchangeLevelChange(record, 1)}
              style={{ minWidth: 28, padding: "0 4px" }}
            >
              +
            </Button>
          </span>
        );
      },
    },
    {
      title: "Прием товаров",
      dataIndex: "import_products",
      width: 130,
      render: (_, record) => (
        <Switch
          checked={!!record.import_products}
          onChange={(e) => handlerSwitchChange(record, "import_products", e)}
        />
      ),
    },
    {
      title: "Выгрузка товаров",
      dataIndex: "export_products",
      width: 140,
      render: (v) => (v != null ? Number(v).toLocaleString("ru-RU") : "0"),
    },
    {
      title: "Выгружено заказов",
      dataIndex: "orders_exported_count",
      width: 140,
      render: (v) => (v != null ? Number(v).toLocaleString("ru-RU") : "0"),
    },
    {
      title: "Создано",
      dataIndex: "created_at",
      width: 160,
      render: (_, record) =>
        record.created_at
          ? dayjs(record.created_at).format("DD-MM-YYYY HH:mm:ss")
          : "-",
    },
    {
      title: "Действие",
      key: "action",
      width: 180,
      fixed: "right",
      render: (_, record) => {
        const menuItems = [];
        const id = record.id;

        if (record.import_products) {
          menuItems.push({
            key: `import_catalog:${id}`,
            label: "Загрузить товары (приём)",
            icon: <SyncOutlined />,
          });
        }
        if (record.export_products) {
          menuItems.push({
            key: `catalog:${id}`,
            label: "Выгрузить товары",
            icon: <SyncOutlined />,
          });
        }
        if (record.import_orders) {
          menuItems.push({
            key: `import_sale:${id}`,
            label: "Загрузить заказы (приём)",
            icon: <SyncOutlined />,
          });
        }
        if (record.export_orders) {
          menuItems.push({
            key: `sale:${id}`,
            label: "Выгрузить заказы",
            icon: <SyncOutlined />,
          });
        }
        if (menuItems.length === 0) {
          menuItems.push({
            key: "disabled",
            label: "Включите приём или выгрузку в настройках",
            disabled: true,
          });
        }

        const isThisSyncing = syncing && syncing.id === record.id;

        return (
          <>
            <Tooltip title="Синхронизировать">
              <Dropdown
                menu={{
                  items: menuItems,
                  onClick: onSyncMenuClick,
                }}
                trigger={["click"]}
              >
                <Button
                  icon={<SyncOutlined spin={isThisSyncing} />}
                  style={{ marginRight: 8 }}
                  loading={isThisSyncing}
                />
              </Dropdown>
            </Tooltip>
            <Tooltip title="Сбросить пароль (Basic Auth для этого подключения)">
              <span>
                <Popconfirm
                  title="Сбросить пароль для этого подключения? Будет сгенерирован новый пароль, старый перестанет действовать. Новый пароль появится в колонке «Пароль»."
                  onConfirm={async () => {
                    console.log("Popconfirm подтверждён, вызываю handleResetPassword");
                    await handleResetPassword(record);
                  }}
                  okText="Сбросить"
                  cancelText="Отмена"
                  okButtonProps={{ loading: resettingPassword === record.id }}
                >
                  <Button
                    icon={<KeyOutlined />}
                    style={{ marginRight: 8 }}
                    loading={resettingPassword === record.id}
                  />
                </Popconfirm>
              </span>
            </Tooltip>
            <Popconfirm
              title="Подтвердите удаление"
              onConfirm={() => handleDelete(record.id)}
              cancelText="Отмена"
              okText="OK"
            >
              <Button
                icon={<DeleteOutlined />}
                danger
                style={{ marginRight: 8 }}
              />
            </Popconfirm>
            <CommerceMLModalUpdate
              record={record}
              token={token}
              onSave={handleConnectionUpdated}
            />
          </>
        );
      },
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <div>
        <CommerceMLModalCreate token={token} onSave={handleConnectionCreated} />
      </div>
      <Table
        dataSource={connections}
        loading={loading}
        columns={columns}
        title={() => "CommerceML подключения"}
        size="middle"
        rowKey="id"
        scroll={{ x: 1500 }}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} из ${total}`,
          locale: { items_per_page: "на стр." },
        }}
      />
    </div>
  );
}

export default CommerceML;
